/**
 * Copyright (C) 2020, Merkur India.
 *
 * The program(s) herein may be used and/or copied only with
 * the written permission of Merkur India. or in accordance with
 * the terms and conditions stipulated in the agreement/contract
 * under which the program(s) have been supplied.
 *
 * $Id: ComponentBuilder.ts ,v 1.0 Exp $
 * Date Author Changes
 * 3 July 2020 Sujata Nethani Created
 *
 */
namespace Utility {
    /**
   * ComponentBuilder Custom class created.
   * @author <a href=mailto:snethani@gauselmann.com>Sujata Nethani</a>
   * @version $Revision: 1.0 $ $Date:  3 July 2020 $
   */
    import Symbol = Symbols.Symbol;
    import BasicWinAnimation = WinAnimation.BasicWinAnimation;
    import ReelContainer = Reels.BSReelContainer;
    import PaytableView = Paytable.PaytableView;
    import GamePaytable = Paytable.GamePaytable;
    import PaytableStyle = Paytable.PaytableStyle;
    import InitResponse = DTO.InitResponse;
    import SwitchableBackground = UIComponent.SwitchableBackground;
    import GameState = States.GameState;
    import Utils = Util.Utils;


    interface TextureKey{
        key:string,
        frame:string
    }

    export class ComponentBuilder {
        public game: Game.AdpPhaserGame;
        public layoutConstants: typeof BS.HRGameLayoutConstants | typeof BS.SDGameLayoutConstants;
        public logo: Phaser.Image;

        constructor(game: Game.AdpPhaserGame) {
            this.game = game;
            if (Util.DeviceInformation.isMobile()) {
                this.layoutConstants = BS.SDGameLayoutConstants;
            } else {
                this.layoutConstants = BS.HRGameLayoutConstants;
            }

        }

        /**
        * Creates the winAnimation including winLines and lineTags
        * @Override
        */
        public createWinAnimation(availableSymbols, background): BasicWinAnimation {
            let winAnimation = new WinAnimation.BasicWinAnimation(new WinAnimation.WinAnimationView(this.game , {
                winAmountBox: {x: this.layoutConstants.WIN_AMOUNT_BOX.x, y: this.layoutConstants.WIN_AMOUNT_BOX.y}
            }));
            winAnimation.registerAvailableSymbols(availableSymbols);

            winAnimation.initialPhaseId = WinAnimation.WinAnimationPhaseId.SETUP;
            let setUpWinsPhase: WinAnimation.Phase.SetUpWinsPhase = new WinAnimation.Phase.SetUpWinsPhase(this.game, WinAnimation.WinAnimationPhaseId.SETUP, undefined, WinAnimation.WinAnimationPhaseId.BELL);
            let bellPhase: WinAnimation.Phase.BSBellPhase = new WinAnimation.Phase.BSBellPhase(this.game, WinAnimation.WinAnimationPhaseId.BELL, WinAnimation.WinAnimationPhaseId.LINES);
            let linePhase: WinAnimation.Phase.LinePhase = new WinAnimation.Phase.BSLinePhase(this.game, WinAnimation.WinAnimationPhaseId.LINES, undefined, WinAnimation.WinAnimationPhaseId.SUMMARY);
            let summaryPhase: WinAnimation.Phase.BSSummaryPhase = new WinAnimation.Phase.BSSummaryPhase(this.game, WinAnimation.WinAnimationPhaseId.SUMMARY, WinAnimation.WinAnimationPhaseId.IDLE);
            let idlePhase: WinAnimation.Phase.IdlePhase = new WinAnimation.Phase.BSIdlePhase(this.game, WinAnimation.WinAnimationPhaseId.IDLE);

            winAnimation.registerAnimationPhase(setUpWinsPhase);
            winAnimation.registerAnimationPhase(bellPhase);
            winAnimation.registerAnimationPhase(linePhase);
            winAnimation.registerAnimationPhase(summaryPhase);
            winAnimation.registerAnimationPhase(idlePhase);
            winAnimation.registerAnimationPhase(new WinAnimation.Phase.ClearedPhase(this.game, WinAnimation.WinAnimationPhaseId.CLEANED, WinAnimation.WinAnimationPhaseId.CLEANED));
            summaryPhase.addTriggerSound(new Sounds.Win.OddsDependendSound("BS_WinTiny", 2.99));
            summaryPhase.addTriggerSound(new Sounds.Win.OddsDependendSound("BS_WinVerySmall", 6.99));
            summaryPhase.addTriggerSound(new Sounds.Win.OddsDependendSound("BS_WinSmall", 14.99));
            summaryPhase.addTriggerSound(new Sounds.Win.OddsDependendSound("BS_WinMedium", 38.99));
            summaryPhase.addTriggerSound(new Sounds.Win.OddsDependendSound("BS_WinBig", 78.99));
            summaryPhase.addTriggerSound(new Sounds.Win.OddsDependendSound("BS_WinHuge", 199.99));
            summaryPhase.addTriggerSound(new Sounds.Win.OddsDependendSound("BS_WinTop", Number.MAX_VALUE));

            let winLineBuilder = new WinAnimation.Line.BSWinLineBuilder(this.game);
            for (let i = 1; i <= 5; i++) {
                let spr = this.getSpriteName("BLSU_feld_linie", "BS_feld_linie");
                let line: WinAnimation.Line.WinLine;
                line = winLineBuilder.createWinLine(i, "blsu_line_gfx", "border" + i);
                winLineBuilder.createLineGfx(line, this.layoutConstants.LINE_X, this.layoutConstants.LINEY[i - 1] - this.layoutConstants.LINE_Y, "blsu_line_gfx", "line" + i);
                winLineBuilder.createLeftTag(line, this.layoutConstants.TAG_LEFT_X, this.layoutConstants.TAGLEFTY[i - 1] - this.layoutConstants.LINE_Y, "blsu_line_gfx", spr + i);
                winLineBuilder.createRightTag(line, this.layoutConstants.TAG_RIGHT_X, this.layoutConstants.TAGRIGHTY[i - 1] - this.layoutConstants.LINE_Y, "blsu_line_gfx", spr + i);
                winAnimation.addWinLine(line);
            }
            return winAnimation;
        }

        /**
         * Create the ReelContainer add all the reels and sounds for it
         * @Override
         */
        public createReelContainer(availableSymbols): ReelContainer {
            let rc: ReelContainer = new ReelContainer(this.game);
            rc.darkeningColor = 0x000000;
            rc.darkeningAmount = 0.5;
            rc.addReel(new Reels.BSReel(this.game, availableSymbols, 0, 3, this.layoutConstants.REELOFFSET_X, this.layoutConstants.REELOFFSET_Y, this.layoutConstants.SYMBOL_WIDTH, this.layoutConstants.SYMBOL_HEIGHT));
            rc.addReel(new Reels.BSReel(this.game, availableSymbols, 1, 3, this.layoutConstants.REELOFFSET_X + (this.layoutConstants.SYMBOL_WIDTH + this.layoutConstants.REELOFFSET_GAP), this.layoutConstants.REELOFFSET_Y, this.layoutConstants.SYMBOL_WIDTH, this.layoutConstants.SYMBOL_HEIGHT));
            rc.addReel(new Reels.BSReel(this.game, availableSymbols, 2, 3, this.layoutConstants.REELOFFSET_X + 2 * (this.layoutConstants.SYMBOL_WIDTH + this.layoutConstants.REELOFFSET_GAP), this.layoutConstants.REELOFFSET_Y, this.layoutConstants.SYMBOL_WIDTH, this.layoutConstants.SYMBOL_HEIGHT));
            rc.addReel(new Reels.BSReel(this.game, availableSymbols, 3, 3, this.layoutConstants.REELOFFSET_X + 3 * (this.layoutConstants.SYMBOL_WIDTH + this.layoutConstants.REELOFFSET_GAP), this.layoutConstants.REELOFFSET_Y, this.layoutConstants.SYMBOL_WIDTH, this.layoutConstants.SYMBOL_HEIGHT));
            rc.addReel(new Reels.BSReel(this.game, availableSymbols, 4, 3, this.layoutConstants.REELOFFSET_X + 4 * (this.layoutConstants.SYMBOL_WIDTH + this.layoutConstants.REELOFFSET_GAP), this.layoutConstants.REELOFFSET_Y, this.layoutConstants.SYMBOL_WIDTH, this.layoutConstants.SYMBOL_HEIGHT));
            rc.setReelSpinSounds(["Walzenlauf1", "Walzenlauf2", "Walzenlauf3", "Walzenlauf4"]);

            return rc;
        }

        /**
         * Create the paytableView
         * @Override
         */
        public createPaytable(stateDTO): PaytableView {
            let initResponse: InitResponse = stateDTO.getValue(DTO.StateExchangeDataKeys.INIT_RESPONSE) as InitResponse;
            var payTableImage = new Phaser.Image(this.game, 0, 0, "blsu_wp_hg");
            var paytableSty;
            if (Util.DeviceInformation.isMobile()) {
                paytableSty = PaytableStyle.PAYTABLE_SD;
            } else {
                paytableSty = PaytableStyle.PAYTABLE_HD;
            }
            return new GamePaytable(this.game, payTableImage, initResponse.payTable, paytableSty);
        }

        public createBackground(): SwitchableBackground {
            let spr = this.getSpriteName("blsu_hg", "bs_hg");
            let background = new Phaser.Image(this.game, this.layoutConstants.BACKGROUND_X, this.layoutConstants.BACKGROUND_Y, spr);
            return new SwitchableBackground(background, null, false);
        }

        /**
        * Create all the symbols for the game in this method together with its reel and win animations
        * To make the creation easier you can use the utility function of the SymbolBuilder
        * @Override
        */
        public createHRSymbols(): Symbol[] {
            let symbolArray: Symbol[] = [];
            let symbol: Symbol;
            let win2xAnimation: Symbols.SymbolAnimation;
            let win5xAnimation: Symbols.SymbolAnimation;
            
            let cherry5xArr = ['BLSU_Cherry_01','BLSU_Cherry_06','BLSU_Cherry_07','BLSU_Cherry_08','BLSU_Cherry_09', 'BLSU_Cherry_08', 'BLSU_Cherry_07', 'BLSU_Cherry_06'];
            let cherry2xArr = ['BLSU_Cherry_01','BLSU_Cherry_02','BLSU_Cherry_03','BLSU_Cherry_04','BLSU_Cherry_05', 'BLSU_Cherry_04', 'BLSU_Cherry_03', 'BLSU_Cherry_02'];
            
            let orange5xArr = ['BLSU_Orange_01','BLSU_Orange_06','BLSU_Orange_07','BLSU_Orange_08', 'BLSU_Orange_09', 'BLSU_Orange_08', 'BLSU_Orange_07','BLSU_Orange_06'];
            let orange2xArr = ['BLSU_Orange_01','BLSU_Orange_02','BLSU_Orange_03','BLSU_Orange_04', 'BLSU_Orange_05', 'BLSU_Orange_04', 'BLSU_Orange_03','BLSU_Orange_02'];
            
            let lemon5xArr = ['BLSU_Lemon_01','BLSU_Lemon_06','BLSU_Lemon_07','BLSU_Lemon_08', 'BLSU_Lemon_09', 'BLSU_Lemon_08', 'BLSU_Lemon_07','BLSU_Lemon_06'];
            let lemon2xArr = ['BLSU_Lemon_01','BLSU_Lemon_02','BLSU_Lemon_03','BLSU_Lemon_04', 'BLSU_Lemon_05', 'BLSU_Lemon_04', 'BLSU_Lemon_03','BLSU_Lemon_02'];
            
            let plum5xArr = ['BLSU_Plum_01','BLSU_Plum_06','BLSU_Plum_07','BLSU_Plum_08', 'BLSU_Plum_09', 'BLSU_Plum_08', 'BLSU_Plum_07','BLSU_Plum_06'];
            let plum2xArr = ['BLSU_Plum_01','BLSU_Plum_02','BLSU_Plum_03','BLSU_Plum_04', 'BLSU_Plum_05', 'BLSU_Plum_04', 'BLSU_Plum_03','BLSU_Plum_02'];
            
            let grapes5xArr = ['BLSU_Grapes_01','BLSU_Grapes_06','BLSU_Grapes_07','BLSU_Grapes_08', 'BLSU_Grapes_09', 'BLSU_Grapes_08', 'BLSU_Grapes_07','BLSU_Grapes_06'];
            let grapes2xArr = ['BLSU_Grapes_01','BLSU_Grapes_02','BLSU_Grapes_03','BLSU_Grapes_04', 'BLSU_Grapes_05', 'BLSU_Grapes_04', 'BLSU_Grapes_03','BLSU_Grapes_02'];
            
            let melon5xArr = ['BLSU_Melon_01','BLSU_Melon_06','BLSU_Melon_07','BLSU_Melon_08', 'BLSU_Melon_09', 'BLSU_Melon_08', 'BLSU_Melon_07','BLSU_Melon_06'];
            let melon2xArr = ['BLSU_Melon_01','BLSU_Melon_02','BLSU_Melon_03','BLSU_Melon_04', 'BLSU_Melon_05', 'BLSU_Melon_04', 'BLSU_Melon_03','BLSU_Melon_02'];
            
            let sun2xArr = ['BLSU_Sun_01','BLSU_Sun_02','BLSU_Sun_03','BLSU_Sun_04', 'BLSU_Sun_05', 'BLSU_Sun_06', 'BLSU_Sun_07','BLSU_Sun_08','BLSU_Sun_09','BLSU_Sun_10', 'BLSU_Sun_11', 'BLSU_Sun_12', 'BLSU_Sun_13', 'BLSU_Sun_14', 'BLSU_Sun_15', 'BLSU_Sun_16'];
            let sun5xArr = ['BLSU_Sun_01', 'BLSU_Sun_17', 'BLSU_Sun_18', 'BLSU_Sun_19','BLSU_Sun_20','BLSU_Sun_21','BLSU_Sun_22', 'BLSU_Sun_23', 'BLSU_Sun_24', 'BLSU_Sun_25','BLSU_Sun_26','BLSU_Sun_27','BLSU_Sun_28', 'BLSU_Sun_29', 'BLSU_Sun_30', 'BLSU_Sun_31'];
                        
            symbol = this.game.symbolBuilder.buildSymbolWithReelGfx(BS.GameConstants.CHERRY, 'blsu_reel_gfx',
                ["BLSU_Cherry_single_HD"], ["BLSU_Cherry_single_HD"]);                
            win5xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.CHERRY, "blsu_animation_gfx", cherry5xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([5]));
            symbol.registerWinAnimation(win5xAnimation);            
            win2xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.ORANGE, "blsu_animation_gfx", cherry2xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([2,3,4]));
            symbol.registerWinAnimation(win2xAnimation);            
            symbolArray.push(symbol);   
                   
            symbol = this.game.symbolBuilder.buildSymbolWithReelGfx(BS.GameConstants.ORANGE, 'blsu_reel_gfx',
                ["BLSU_Orange_single_HD"], ["BLSU_Orange_single_HD"]);                
            win5xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.ORANGE, "blsu_animation_gfx", orange5xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([5]));
            symbol.registerWinAnimation(win5xAnimation);            
            win2xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.ORANGE, "blsu_animation_gfx", orange2xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([3, 4]));
            symbol.registerWinAnimation(win2xAnimation);            
            symbolArray.push(symbol); 
                     
            symbol = this.game.symbolBuilder.buildSymbolWithReelGfx(BS.GameConstants.LEMON, 'blsu_reel_gfx',
                ["BLSU_Lemon_single_HD"], ["BLSU_Lemon_single_HD"]);                
            win5xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.LEMON, "blsu_animation_gfx", lemon5xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([5]));
            symbol.registerWinAnimation(win5xAnimation);            
            win2xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.LEMON, "blsu_animation_gfx", lemon2xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([3, 4]));
            symbol.registerWinAnimation(win2xAnimation);            
            symbolArray.push(symbol); 
                     
            symbol = this.game.symbolBuilder.buildSymbolWithReelGfx(BS.GameConstants.PLUM, 'blsu_reel_gfx',
                ["BLSU_Plum_single_HD"], ["BLSU_Plum_single_HD"]);                
            win5xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.PLUM, "blsu_animation_gfx", plum5xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([5]));
            symbol.registerWinAnimation(win5xAnimation);            
            win2xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.PLUM, "blsu_animation_gfx", plum2xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([3, 4]));
            symbol.registerWinAnimation(win2xAnimation);            
            symbolArray.push(symbol);  
              
            symbol = this.game.symbolBuilder.buildSymbolWithReelGfx(BS.GameConstants.GRAPES, 'blsu_reel_gfx',
                ["BLSU_Grapes_single_HD"], ["BLSU_Grapes_single_HD"]);                
            win5xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.GRAPES, "blsu_animation_gfx", grapes5xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([5]));
            symbol.registerWinAnimation(win5xAnimation);            
            win2xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.GRAPES, "blsu_animation_gfx", grapes2xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([3, 4]));
            symbol.registerWinAnimation(win2xAnimation);            
            symbolArray.push(symbol);  
              
            symbol = this.game.symbolBuilder.buildSymbolWithReelGfx(BS.GameConstants.MELON, 'blsu_reel_gfx',
                ["BLSU_Melon_single_HD"], ["BLSU_Melon_single_HD"]);                
            win5xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.MELON, "blsu_animation_gfx", melon5xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([5]));
            symbol.registerWinAnimation(win5xAnimation);            
            win2xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.MELON, "blsu_animation_gfx", melon2xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([3, 4]));
            symbol.registerWinAnimation(win2xAnimation);            
            symbolArray.push(symbol);    
            
            symbol = this.game.symbolBuilder.buildSymbolWithReelGfx(BS.GameConstants.SUN, 'blsu_reel_gfx',
                ["BLSU_Sun_single_HD"], ["BLSU_Sun_single_HD"]);                
            win5xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.SUN, "blsu_animation_gfx", sun5xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([5]));
            symbol.registerWinAnimation(win5xAnimation);            
            win2xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.SUN, "blsu_animation_gfx", sun2xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([3, 4]));
            symbol.registerWinAnimation(win2xAnimation);            
            symbolArray.push(symbol);  

            return symbolArray;
        }

        public createSDSymbols(): Symbol[] {
            let symbolArray: Symbol[] = [];
            let symbol: Symbol;
            let win2xAnimation: Symbols.SymbolAnimation;
            let win5xAnimation: Symbols.SymbolAnimation;
            
            let cherry5xArr = ['BS_Cherry_01','BS_Cherry_06','BS_Cherry_07','BS_Cherry_08','BS_Cherry_09', 'BS_Cherry_08', 'BS_Cherry_07', 'BS_Cherry_06'];
            let cherry2xArr = ['BS_Cherry_01','BS_Cherry_02','BS_Cherry_03','BS_Cherry_04','BS_Cherry_05', 'BS_Cherry_04', 'BS_Cherry_03', 'BS_Cherry_02'];
            
            let orange5xArr = ['BS_Orange_01','BS_Orange_06','BS_Orange_07','BS_Orange_08', 'BS_Orange_09', 'BS_Orange_08', 'BS_Orange_07','BS_Orange_06'];
            let orange2xArr = ['BS_Orange_01','BS_Orange_02','BS_Orange_03','BS_Orange_04', 'BS_Orange_05', 'BS_Orange_04', 'BS_Orange_03','BS_Orange_02'];
            
            let lemon5xArr = ['BS_Lemon_01','BS_Lemon_06','BS_Lemon_07','BS_Lemon_08', 'BS_Lemon_09', 'BS_Lemon_08', 'BS_Lemon_07','BS_Lemon_06'];
            let lemon2xArr = ['BS_Lemon_01','BS_Lemon_02','BS_Lemon_03','BS_Lemon_04', 'BS_Lemon_05', 'BS_Lemon_04', 'BS_Lemon_03','BS_Lemon_02'];
            
            let plum5xArr = ['BS_Plum_01','BS_Plum_06','BS_Plum_07','BS_Plum_08', 'BS_Plum_09', 'BS_Plum_08', 'BS_Plum_07','BS_Plum_06'];
            let plum2xArr = ['BS_Plum_01','BS_Plum_02','BS_Plum_03','BS_Plum_04', 'BS_Plum_05', 'BS_Plum_04', 'BS_Plum_03','BS_Plum_02'];
            
            let grapes5xArr = ['BS_Grapes_01','BS_Grapes_06','BS_Grapes_07','BS_Grapes_08', 'BS_Grapes_09', 'BS_Grapes_08', 'BS_Grapes_07','BS_Grapes_06'];
            let grapes2xArr = ['BS_Grapes_01','BS_Grapes_02','BS_Grapes_03','BS_Grapes_04', 'BS_Grapes_05', 'BS_Grapes_04', 'BS_Grapes_03','BS_Grapes_02'];
            
            let melon5xArr = ['BS_Melon_01','BS_Melon_06','BS_Melon_07','BS_Melon_08', 'BS_Melon_09', 'BS_Melon_08', 'BS_Melon_07','BS_Melon_06'];
            let melon2xArr = ['BS_Melon_01','BS_Melon_02','BS_Melon_03','BS_Melon_04', 'BS_Melon_05', 'BS_Melon_04', 'BS_Melon_03','BS_Melon_02'];
            
            let sun2xArr = ['BS_Sun_01','BS_Sun_02','BS_Sun_03','BS_Sun_04', 'BS_Sun_05', 'BS_Sun_06', 'BS_Sun_07','BS_Sun_08','BS_Sun_09','BS_Sun_10', 'BS_Sun_11', 'BS_Sun_12', 'BS_Sun_13', 'BS_Sun_14', 'BS_Sun_15', 'BS_Sun_16'];
            let sun5xArr = ['BS_Sun_01', 'BS_Sun_17', 'BS_Sun_18', 'BS_Sun_19','BS_Sun_20','BS_Sun_21','BS_Sun_22', 'BS_Sun_23', 'BS_Sun_24', 'BS_Sun_25','BS_Sun_26','BS_Sun_27','BS_Sun_28', 'BS_Sun_29', 'BS_Sun_30', 'BS_Sun_31'];
                        
            symbol = this.game.symbolBuilder.buildSymbolWithReelGfx(BS.GameConstants.CHERRY, 'blsu_reel_gfx',
                ["BS_Cherry_single_HD"], ["BS_Cherry_single_HD"]);                
            win5xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.CHERRY, "blsu_animation_gfx", cherry5xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([5]));
            symbol.registerWinAnimation(win5xAnimation);            
            win2xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.ORANGE, "blsu_animation_gfx", cherry2xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([2,3,4]));
            symbol.registerWinAnimation(win2xAnimation);            
            symbolArray.push(symbol);   
                   
            symbol = this.game.symbolBuilder.buildSymbolWithReelGfx(BS.GameConstants.ORANGE, 'blsu_reel_gfx',
                ["BS_Orange_single_HD"], ["BS_Orange_single_HD"]);                
            win5xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.ORANGE, "blsu_animation_gfx", orange5xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([5]));
            symbol.registerWinAnimation(win5xAnimation);            
            win2xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.ORANGE, "blsu_animation_gfx", orange2xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([3, 4]));
            symbol.registerWinAnimation(win2xAnimation);            
            symbolArray.push(symbol); 
                     
            symbol = this.game.symbolBuilder.buildSymbolWithReelGfx(BS.GameConstants.LEMON, 'blsu_reel_gfx',
                ["BS_Lemon_single_HD"], ["BS_Lemon_single_HD"]);                
            win5xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.LEMON, "blsu_animation_gfx", lemon5xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([5]));
            symbol.registerWinAnimation(win5xAnimation);            
            win2xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.LEMON, "blsu_animation_gfx", lemon2xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([3, 4]));
            symbol.registerWinAnimation(win2xAnimation);            
            symbolArray.push(symbol); 
                     
            symbol = this.game.symbolBuilder.buildSymbolWithReelGfx(BS.GameConstants.PLUM, 'blsu_reel_gfx',
                ["BS_Plum_single_HD"], ["BS_Plum_single_HD"]);                
            win5xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.PLUM, "blsu_animation_gfx", plum5xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([5]));
            symbol.registerWinAnimation(win5xAnimation);            
            win2xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.PLUM, "blsu_animation_gfx", plum2xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([3, 4]));
            symbol.registerWinAnimation(win2xAnimation);            
            symbolArray.push(symbol);  
              
            symbol = this.game.symbolBuilder.buildSymbolWithReelGfx(BS.GameConstants.GRAPES, 'blsu_reel_gfx',
                ["BS_Grapes_single_HD"], ["BS_Grapes_single_HD"]);                
            win5xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.GRAPES, "blsu_animation_gfx", grapes5xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([5]));
            symbol.registerWinAnimation(win5xAnimation);            
            win2xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.GRAPES, "blsu_animation_gfx", grapes2xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([3, 4]));
            symbol.registerWinAnimation(win2xAnimation);            
            symbolArray.push(symbol);  
              
            symbol = this.game.symbolBuilder.buildSymbolWithReelGfx(BS.GameConstants.MELON, 'blsu_reel_gfx',
                ["BS_Melon_single_HD"], ["BS_Melon_single_HD"]);                
            win5xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.MELON, "blsu_animation_gfx", melon5xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([5]));
            symbol.registerWinAnimation(win5xAnimation);            
            win2xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.MELON, "blsu_animation_gfx", melon2xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([3, 4]));
            symbol.registerWinAnimation(win2xAnimation);            
            symbolArray.push(symbol);    
            
            symbol = this.game.symbolBuilder.buildSymbolWithReelGfx(BS.GameConstants.SUN, 'blsu_reel_gfx',
                ["BS_Sun_single_HD"], ["BS_Sun_single_HD"]);                
            win5xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.SUN, "blsu_animation_gfx", sun5xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([5]));
            symbol.registerWinAnimation(win5xAnimation);            
            win2xAnimation = this.game.symbolBuilder.createWinSymbolAnimation(BS.GameConstants.SUN, "blsu_animation_gfx", sun2xArr, BS.GameConstants.FPS, new Symbols.OccurrenceTrigger([3, 4]));
            symbol.registerWinAnimation(win2xAnimation);            
            symbolArray.push(symbol);  

            return symbolArray;
        }

        getSpriteName(desk, mob): string {
            if (Util.DeviceInformation.isMobile()) {
                return mob;
            } else {
                return desk;
            }
        }

    }
}